import * as React from "react";
import {
  ReferenceInput,
  SelectInput,
  TextInput,
  useRecordContext,
  useGetOne,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  NumberInput,
  useUpdate,
  useCreate,
  SaveButton,
  DeleteButton,
  Toolbar,
  AutocompleteInput,
  required,
  useNotify,
  useRedirect
} from "react-admin";
import { InputSeparator, TinyMCEContentInput } from '../../components/input';
import { CircularProgress, Grid, Button } from "@mui/material";
import Transformer from "../../common/Transformer";
import {
  StatusEnum,
  ProductTypeEnum,
  ProductVisibilityEnum
} from "../../common/enum";
import { ProductEditComponent } from "./ProductComponent";
import { PriceForm } from "./PriceForm";
import { CustomField } from "./CustomField";
import {
  attributesToCamilCase,
  clearEmptyAttributes,
  clearEmptyNaNAttributes,
} from "src/common/UtilFunctions";
import { GalleryTab } from "./GalleryTab";
import { Edit, FormTab, TabbedForm, IfCanAccess } from "src/enterprise/ra-rbac";
import { Table, TableHead, TableBody, TableContainer, TableCell, TableRow, Paper  } from "@mui/material";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";

const ProductEditToolbar = () => {
  return (
    <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
      <SaveButton alwaysEnable />
      <IfCanAccess action="delete">
        <DeleteButton />
      </IfCanAccess>
    </Toolbar>
  );
};

const ProductConfigurableProducts = (props) => {
  const record = props.record;

  const redirect = useRedirect();

  function redirectToProduct(id) {
    redirect("/admin/products/" + id);
  }

  if ((typeof record?.children == "undefined" || record?.children?.length == 0) || 
  (typeof record?.configurable_attributes  == "undefined" || record?.configurable_attributes?.length == 0)) {
    return null;
  }

  return ( 
  <TableContainer component={Paper}>
    <Table sx={{ minWidth: 650 }}>
      <TableHead>
        <TableRow>
          <TableCell>Product</TableCell>
          {record.configurable_attributes.map((attribute: any) => (
            <TableCell key={attribute.code}>{attribute.name}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {record.children.map((child) => (
          <TableRow
            key={child.id}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCell component="th" scope="row">
              <Button
                onClick={() => redirectToProduct(child.id)}
                variant="outlined"
                startIcon={<LaunchOutlinedIcon />}
              >
                {`${child.name} - ${child.sku}`}
              </Button>
            </TableCell>
            {record.configurable_attributes.map((attribute: any) => {
              let attributeValue = child.attributes[attribute.code] ?? "";

              return <TableCell key={attribute.code}>{Array.isArray(attributeValue) ? attributeValue.join(', ') : attributeValue}</TableCell>
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
  )
}

const ProductEditForm = (props) => {
  const record = useRecordContext(props);
  const setGalleryItems = props.setGalleryItems;
  let attributeId = record?.attributeSet
    ? record.attributeSet
    : record?.attribute_set.id;
  const {
    data: attributeSetData,
    isLoading,
    error,
  } = useGetOne("admin/product/attribute-sets", { id: attributeId });
  const filterToQuery = (searchText, recordId) => ({
    "search": searchText,
    "id.not": recordId,
  });
  const notify = useNotify();

  if (isLoading || error) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <CircularProgress size={40} />
      </div>
    );
  }
  
  const configAttributes = attributeSetData?.attributes.filter(att =>
    att.code.startsWith("config_")
  );

  const otherAttributes = attributeSetData?.attributes.filter(
    att => !att.code.startsWith("config_")
  );

  return (
    <>
      <TabbedForm sanitizeEmptyValues toolbar={<ProductEditToolbar />}>
        <FormTab label="General" name="general">
          <Grid container rowSpacing={1} columnSpacing={2}>
            <Grid item sm={6} md={3}>
              <TextInput disabled source="id"/>
            </Grid>
            <Grid item sm={6} lg={3}>
              <TextInput source="legacy_id"/>
            </Grid>
            <Grid item sm={12} md={6}>
              <TextInput disabled source="attribute_set.name" label="Attribute Set" />
            </Grid>
            <Grid container item rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <TextInput source="name" validate={required()}/>
              </Grid>
              <Grid item sm={12} md={6}>
                <ReferenceInput
                  source="parent.id"
                  reference="admin/products"
                  filter={{ parent: null, "id.not": record?.id }}
                  perPage={50}
                >
                  <AutocompleteInput
                    filterToQuery={(e) => filterToQuery(e, record?.id)}
                    optionText={(record) => record.sku + " " + record.name}
                    size="small"
                    label="Parent"
                    defaultValue=""
                  />
                </ReferenceInput>
              </Grid>
            </Grid>
            <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12}> 
                <TinyMCEContentInput source="description" required fullWidth validate={required()}/>
              </Grid>
              <Grid item sm={12}> 
                <TinyMCEContentInput source="short_description" required fullWidth validate={required()}/>
              </Grid>   
            </Grid>
            <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
                <Grid item sm={12}>
                  <SelectInput source="visibility" choices={ProductVisibilityEnum} validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <TextInput source="slug" validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <TextInput source="sku" validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <SelectInput disabled source="type" choices={ProductTypeEnum} validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <NumberInput source="weight" min={0} validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <SelectInput source="status" choices={StatusEnum} validate={required()} />
                </Grid>
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="SEO" name="seo">
          <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
            <Grid item sm={12}>
              <TextInput source="meta_title"/>
            </Grid>
            <Grid item sm={12}>
              <TextInput
                source="meta_description"
                rows={4}
                fullWidth
                multiline
              />
            </Grid>
            <Grid item sm={12}>
              <TextInput source="meta_keywords" multiline rows={4}/>
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="Categories" name="categories">
          <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
            <Grid item sm={12}>
              <ReferenceArrayInput
                source="category_ids"
                reference="admin/product/categories"
                children={
                  <AutocompleteArrayInput
                    filterToQuery={(e) => filterToQuery(e, record?.id)}
                    optionText="name"
                    label="Categories"
                    size="small"
                    sx={{ minWidth: "35vw" }}
                  />
                }
              />
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="Stickers" name="stickers">
          <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
            <Grid item sm={12}>
              <ReferenceArrayInput
                source="sticker_ids"
                reference="admin/product/stickers"
                children={
                  <AutocompleteArrayInput
                    filterToQuery={(e) => filterToQuery(e, record?.id)}
                    optionText="name"
                    label="Stickers"
                    size="small"
                    sx={{ minWidth: "35vw" }}
                  />
                }
              />
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="Connected Products" name="connected_products">
          <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
            <Grid item sm={12}>
              <ReferenceArrayInput
                source="related_product_ids"
                reference="admin/products"
              >
                <AutocompleteArrayInput
                  filterToQuery={(e) => filterToQuery(e, record?.id)}
                  size="small"
                  optionText={(record) => record.sku + " " + record.name}
                  sx={{ minWidth: "35vw" }}
                />
              </ReferenceArrayInput>
            </Grid>
            <Grid item sm={12}>
              <ReferenceArrayInput
                source="alternative_product_ids"
                reference="admin/products"
              >
                <AutocompleteArrayInput
                  filterToQuery={(e) => filterToQuery(e, record?.id)}
                  size="small"
                  optionText={(record) => record.sku + " " + record.name}
                  sx={{ minWidth: "35vw" }}
                />
              </ReferenceArrayInput>
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="Attributes" name="attributes">
          <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
            {configAttributes.length > 0 && (
              <>
                <Grid item sm={12} md={12}>
                  <InputSeparator title="Config Attributes" />
                </Grid>
                {configAttributes.map((att, key) => (
                  <Grid item sm={12} md={6} key={`config-${key}`}>
                    <CustomField source={`attributes.${att.code}`} attribute={att} />
                  </Grid>
                ))}
              </>
            )}

            {otherAttributes.length > 0 && (
              <>
                {configAttributes.length > 0 && (
                  <Grid item sm={12} md={12}>
                    <InputSeparator title="" />
                  </Grid>
                )}
                {otherAttributes.map((att, key) => (
                  <Grid item sm={12} md={6} key={`other-${key}`}>
                    <CustomField source={`attributes.${att.code}`} attribute={att} />
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </FormTab>
        <FormTab label="Prices" name="prices">
          <PriceForm />
        </FormTab>
        <FormTab label="Gallery" name="gallery">
          <GalleryTab setGalleryItems={setGalleryItems} />
        </FormTab>
        <FormTab label="Configurable Options" name="configurable_options">
          <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
            <Grid item sm={12}>
              <ReferenceArrayInput
                source="configurable_attribute_ids"
                reference="admin/product/attributes"
                children={
                  <AutocompleteArrayInput
                    filterToQuery={(e) => filterToQuery(e, record?.id)}
                    optionText="name"
                    label="Configurable Attributes"
                    size="small"
                    sx={{ minWidth: "35vw" }}
                  />
                }
              />
            </Grid>
            <Grid item sm={12}>
              <ProductConfigurableProducts record={record} />
            </Grid>
          </Grid>
        </FormTab>
      </TabbedForm>
    </>
  );
};

export const ProductEdit = (props) => {
  const [create, { isLoading: createLoading, error: createError }] =
    useCreate();
  const [update, { data, isLoading: updateLoading, error: updateError }] =
    useUpdate();
  const [galleryItems, setGalleryItems] = React.useState([]);

  const transform = (data, { previousData }) => {
    let productPrice = data?.price;

    Transformer(data, {
      remove: [
        "id",
        "createdAt",
        "createdBy",
        "updatedAt",
        "updatedBy",
        "attributeSet",
        "categoryIds",
        "stickerIds",
        "relatedProductIds",
        "alternativeProductIds",
        "configurableAttributeIds",
        "configurableProducts",
        "children",
        "price",
        "galleryFeatured",
        "gallery",
        "stockQuantity",
        "stock",
        "stocks",
        "priceRange"
      ],
      update: {
        weight: parseFloat(data.weight),
        parent: data.parent.id,
        categories: data["category_ids"],
        stickers: data["sticker_ids"],
        relatedProducts: data["related_product_ids"],
        alternativeProducts: data["alternative_product_ids"],
        configurableAttributes: data["configurable_attribute_ids"],
      },
    });

    if (productPrice) {
      Transformer(productPrice, {
        remove: [
          "id",
          "createdAt",
          "createdBy",
          "updatedAt",
          "updatedBy",
          "tax_class",
          "price_calculator",
        ],
        update: {
          taxClass: productPrice.tax_class.id,
          priceCalculator: productPrice.price_calculator.id,
          product: previousData.id,
        },
      });

      if (previousData.price) updatePrice(productPrice, previousData);
      else createPrice(productPrice);
    }

    if (galleryItems.length > 0) {
      updateGallery(galleryItems);
    }

    clearEmptyAttributes(data.attributes);
    clearEmptyNaNAttributes(data);

    return data;
  };

  const updatePrice = (productPrice, previousData) => {
    attributesToCamilCase(productPrice);
    clearEmptyNaNAttributes(productPrice);
    if (!updateLoading && !updateError)
      update("admin/product/prices", {
        id: previousData.price.id,
        data: productPrice,
        previousData: previousData.price,
      });
  };

  const createPrice = (productPrice) => {
    attributesToCamilCase(productPrice);
    clearEmptyNaNAttributes(productPrice);
    if (!createLoading && !createError)
      create("admin/product/prices", { data: productPrice });
  };

  const updateGallery = (galleryItems) => {
    galleryItems.forEach((item) => {
      let galleryItemId = item.id;
      Transformer(item, {
        remove: ["id", "createdAt", "createdBy", "updatedAt", "updatedBy"],
        update: {
          product: item.product?.id,
          sortOrder: item["sort_order"],
          featured: item.featured.toString(),
        },
      });
      update("admin/product/gallery", { id: galleryItemId, data: item });
    });
  };

  return (
    <Edit
      component={ProductEditComponent}
      transform={transform}
      mutationMode="pessimistic"
      actions={null}
      {...props}
    >
      <ProductEditForm galleryItems={galleryItems} setGalleryItems={setGalleryItems} />
    </Edit>
  );
};
